import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, BarChart3, DoorOpen, Building2, CalendarRange, Crown } from "lucide-react";

// Helpers
function fmtISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}
function toMin(s) { const [h, m] = s.split(":"); return parseInt(h) * 60 + parseInt(m); }
function startOfWeek(d) {
    const x = new Date(d); x.setHours(0, 0, 0, 0);
    const day = (x.getDay() + 6) % 7; // 0 = lunes
    x.setDate(x.getDate() - day);
    return x;
}
function endOfWeek(d) {
    const x = startOfWeek(d); x.setDate(x.getDate() + 6); return x;
}
function startOfMonth(d) {
    const x = new Date(d.getFullYear(), d.getMonth(), 1); x.setHours(0, 0, 0, 0); return x;
}
function endOfMonth(d) {
    const x = new Date(d.getFullYear(), d.getMonth() + 1, 0); x.setHours(0, 0, 0, 0); return x;
}
function diasEntre(desde, hasta) {
    const ms = endOfDay(hasta) - startOfDay(desde);
    return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
}
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function endOfDay(d) { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; }
const DIAS_ES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function ocupacionLabel(p) {
    if (p >= 60) return { label: "Alta", cls: "text-success" };
    if (p >= 30) return { label: "Media", cls: "text-warning" };
    return { label: "Baja", cls: "text-muted-foreground" };
}

export default function MetricasReservas() {
    const [salas, setSalas] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [reservas, setReservas] = useState([]);

    const [rango, setRango] = useState("semana"); // semana, mes, custom
    const [desde, setDesde] = useState("");
    const [hasta, setHasta] = useState("");
    const [salaFiltro, setSalaFiltro] = useState("todas");
    const [unidadFiltro, setUnidadFiltro] = useState("todas");

    // Calcular rango efectivo
    const { rangoDesde, rangoHasta } = useMemo(() => {
        const today = new Date();
        if (rango === "semana") return { rangoDesde: startOfWeek(today), rangoHasta: endOfWeek(today) };
        if (rango === "mes") return { rangoDesde: startOfMonth(today), rangoHasta: endOfMonth(today) };
        if (rango === "custom" && desde && hasta) return { rangoDesde: new Date(desde + "T00:00:00"), rangoHasta: new Date(hasta + "T00:00:00") };
        return { rangoDesde: startOfWeek(today), rangoHasta: endOfWeek(today) };
    }, [rango, desde, hasta]);

    const load = useCallback(() => {
        const params = {
            fecha_desde: fmtISO(rangoDesde),
            fecha_hasta: fmtISO(rangoHasta),
            estado: "confirmada",
        };
        if (salaFiltro !== "todas") params.sala_id = salaFiltro;
        api.get("/reservas", { params }).then((r) => {
            const data = unidadFiltro === "todas" ? r.data : r.data.filter((x) => x.unidad_id === unidadFiltro);
            setReservas(data);
        });
    }, [rangoDesde, rangoHasta, salaFiltro, unidadFiltro]);

    useEffect(() => {
        api.get("/salas").then((r) => setSalas(r.data));
        api.get("/unidades").then((r) => setUnidades(r.data));
    }, []);
    useEffect(() => { load(); }, [load]);

    const salaName = (id) => salas.find((s) => s.sala_id === id)?.nombre || "—";
    const uniName = (id) => unidades.find((u) => u.unidad_id === id)?.etiqueta || "—";

    // Horas total en rango
    const totalHoras = useMemo(() => {
        let h = 0;
        for (const r of reservas) h += (toMin(r.hora_fin) - toMin(r.hora_inicio)) / 60;
        return h;
    }, [reservas]);

    // Capacidad total (horas) en el rango, según salas y sus días disponibles + horario
    const capacidadTotal = useMemo(() => {
        const salasActivas = salas.filter((s) => s.activa && (salaFiltro === "todas" || s.sala_id === salaFiltro));
        let cap = 0;
        const dStart = startOfDay(rangoDesde);
        const dEnd = startOfDay(rangoHasta);
        for (let d = new Date(dStart); d <= dEnd; d.setDate(d.getDate() + 1)) {
            const idx = (d.getDay() + 6) % 7;
            for (const s of salasActivas) {
                if (s.dias_disponibles?.includes(idx)) {
                    cap += (toMin(s.horario_fin) - toMin(s.horario_inicio)) / 60;
                }
            }
        }
        return cap;
    }, [salas, salaFiltro, rangoDesde, rangoHasta]);

    const ocupacionPct = capacidadTotal > 0 ? (totalHoras / capacidadTotal) * 100 : 0;

    // Por sala
    const porSala = useMemo(() => {
        const map = new Map();
        for (const r of reservas) {
            const k = r.sala_id;
            const cur = map.get(k) || { sala_id: k, horas: 0, count: 0, horas_x_inicio: {} };
            cur.horas += (toMin(r.hora_fin) - toMin(r.hora_inicio)) / 60;
            cur.count += 1;
            cur.horas_x_inicio[r.hora_inicio] = (cur.horas_x_inicio[r.hora_inicio] || 0) + 1;
            map.set(k, cur);
        }
        // capacidad por sala en el rango
        const dStart = startOfDay(rangoDesde);
        const dEnd = startOfDay(rangoHasta);
        const arr = [...map.values()].map((row) => {
            const sala = salas.find((s) => s.sala_id === row.sala_id);
            let cap = 0;
            if (sala) {
                for (let d = new Date(dStart); d <= dEnd; d.setDate(d.getDate() + 1)) {
                    const idx = (d.getDay() + 6) % 7;
                    if (sala.dias_disponibles?.includes(idx)) {
                        cap += (toMin(sala.horario_fin) - toMin(sala.horario_inicio)) / 60;
                    }
                }
            }
            const pico = Object.entries(row.horas_x_inicio).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
            return { ...row, capacidad: cap, ocupacion: cap > 0 ? (row.horas / cap) * 100 : 0, pico };
        });
        return arr.sort((a, b) => b.horas - a.horas);
    }, [reservas, salas, rangoDesde, rangoHasta]);

    // Por oficina
    const porUnidad = useMemo(() => {
        const map = new Map();
        for (const r of reservas) {
            const k = r.unidad_id || "_admin_";
            const cur = map.get(k) || { unidad_id: k, horas: 0, count: 0, salas_x: {} };
            cur.horas += (toMin(r.hora_fin) - toMin(r.hora_inicio)) / 60;
            cur.count += 1;
            cur.salas_x[r.sala_id] = (cur.salas_x[r.sala_id] || 0) + 1;
            map.set(k, cur);
        }
        return [...map.values()].map((row) => {
            const salaMas = Object.entries(row.salas_x).sort((a, b) => b[1] - a[1])[0]?.[0];
            return { ...row, sala_mas: salaMas };
        }).sort((a, b) => b.horas - a.horas);
    }, [reservas]);

    // Por horario
    const porHorario = useMemo(() => {
        const map = new Map();
        for (const r of reservas) {
            map.set(r.hora_inicio, (map.get(r.hora_inicio) || 0) + 1);
        }
        const maxCount = Math.max(...[...map.values()], 1);
        return [...map.entries()]
            .map(([hora, count]) => ({ hora, count, pct: (count / maxCount) * 100 }))
            .sort((a, b) => a.hora.localeCompare(b.hora));
    }, [reservas]);

    // Por día de la semana
    const porDia = useMemo(() => {
        const counts = new Array(7).fill(0);
        for (const r of reservas) {
            const d = new Date(r.fecha + "T00:00:00");
            const idx = (d.getDay() + 6) % 7;
            counts[idx] += (toMin(r.hora_fin) - toMin(r.hora_inicio)) / 60;
        }
        return counts;
    }, [reservas]);
    const diaMasUsado = useMemo(() => {
        const max = Math.max(...porDia, 0);
        if (max === 0) return "—";
        return DIAS_ES[porDia.indexOf(max)];
    }, [porDia]);

    const horarioPico = porHorario[0]
        ? [...porHorario].sort((a, b) => b.count - a.count)[0]?.hora
        : "—";

    const oficinaMas = porUnidad[0]
        ? (porUnidad[0].unidad_id === "_admin_" ? "Administración" : uniName(porUnidad[0].unidad_id))
        : "—";

    const salaMas = porSala[0] ? salaName(porSala[0].sala_id) : "—";

    return (
        <div className="space-y-4">
            {/* Filtros */}
            <Card>
                <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Rango</Label>
                        <Select value={rango} onValueChange={setRango}>
                            <SelectTrigger data-testid="met-rango"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="semana">Esta semana</SelectItem>
                                <SelectItem value="mes">Este mes</SelectItem>
                                <SelectItem value="custom">Personalizado</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {rango === "custom" ? (
                        <>
                            <div className="space-y-1"><Label className="text-xs">Desde</Label>
                                <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
                            <div className="space-y-1"><Label className="text-xs">Hasta</Label>
                                <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
                        </>
                    ) : (
                        <div className="md:col-span-2 flex items-center text-xs text-muted-foreground">
                            <CalendarRange className="h-3.5 w-3.5 mr-1.5" />
                            {fmtISO(rangoDesde)} → {fmtISO(rangoHasta)} ({diasEntre(rangoDesde, rangoHasta)} días)
                        </div>
                    )}
                    <div className="space-y-1"><Label className="text-xs">Oficina</Label>
                        <Select value={unidadFiltro} onValueChange={setUnidadFiltro}>
                            <SelectTrigger data-testid="met-unidad"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="todas">Todas</SelectItem>
                                {unidades.map((u) => <SelectItem key={u.unidad_id} value={u.unidad_id}>{u.etiqueta}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Sala</Label>
                        <Select value={salaFiltro} onValueChange={setSalaFiltro}>
                            <SelectTrigger data-testid="met-sala"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="todas">Todas las salas</SelectItem>
                                {salas.map((s) => <SelectItem key={s.sala_id} value={s.sala_id}>{s.nombre}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Kpi icon={Clock} label="Horas reservadas" value={`${totalHoras.toFixed(1)} h`} testid="kpi-horas" />
                <Kpi icon={BarChart3} label="Ocupación" value={`${ocupacionPct.toFixed(0)}%`} testid="kpi-ocup" />
                <Kpi icon={DoorOpen} label="Sala más usada" value={salaMas} testid="kpi-sala" />
                <Kpi icon={Clock} label="Horario más usado" value={horarioPico} testid="kpi-horario" />
                <Kpi icon={CalendarRange} label="Día con más uso" value={diaMasUsado} testid="kpi-dia" />
                <Kpi icon={Building2} label="Oficina que más reserva" value={oficinaMas} testid="kpi-of" />
            </div>

            {/* Tablas */}
            <div className="grid gap-3 lg:grid-cols-2">
                <Card>
                    <CardContent className="p-0">
                        <h3 className="font-heading font-bold text-sm px-4 pt-3 pb-2 border-b border-border">Uso por sala</h3>
                        {porSala.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-6 text-center">Sin datos</p>
                        ) : (
                            <table className="w-full text-[13px]">
                                <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                                    <tr>
                                        <th className="text-left font-semibold px-3 py-2">Sala</th>
                                        <th className="text-right font-semibold px-2 py-2">Horas</th>
                                        <th className="text-right font-semibold px-2 py-2">Reservas</th>
                                        <th className="text-right font-semibold px-2 py-2">Ocup.</th>
                                        <th className="text-right font-semibold px-3 py-2">Pico</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {porSala.map((row) => {
                                        const oc = ocupacionLabel(row.ocupacion);
                                        return (
                                            <tr key={row.sala_id} className="border-t border-border hover:bg-primary-soft/30">
                                                <td className="px-3 py-1.5 truncate max-w-[180px]">{salaName(row.sala_id)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{row.horas.toFixed(1)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono">{row.count}</td>
                                                <td className={`px-2 py-1.5 text-right font-mono ${oc.cls}`}>{row.ocupacion.toFixed(0)}%</td>
                                                <td className="px-3 py-1.5 text-right font-mono">{row.pico}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-0">
                        <h3 className="font-heading font-bold text-sm px-4 pt-3 pb-2 border-b border-border">Uso por oficina</h3>
                        {porUnidad.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-6 text-center">Sin datos</p>
                        ) : (
                            <table className="w-full text-[13px]">
                                <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                                    <tr>
                                        <th className="text-left font-semibold px-3 py-2">Oficina</th>
                                        <th className="text-right font-semibold px-2 py-2">Horas</th>
                                        <th className="text-right font-semibold px-2 py-2">Reservas</th>
                                        <th className="text-left font-semibold px-3 py-2">Sala top</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {porUnidad.map((row) => (
                                        <tr key={row.unidad_id} className="border-t border-border hover:bg-primary-soft/30">
                                            <td className="px-3 py-1.5 truncate">
                                                {row.unidad_id === "_admin_"
                                                    ? <span className="inline-flex items-center gap-1"><Crown className="h-3 w-3 text-accent" /> Administración</span>
                                                    : uniName(row.unidad_id)}
                                            </td>
                                            <td className="px-2 py-1.5 text-right font-mono">{row.horas.toFixed(1)}</td>
                                            <td className="px-2 py-1.5 text-right font-mono">{row.count}</td>
                                            <td className="px-3 py-1.5 truncate max-w-[180px]">{row.sala_mas ? salaName(row.sala_mas) : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardContent className="p-0">
                    <h3 className="font-heading font-bold text-sm px-4 pt-3 pb-2 border-b border-border">Horarios más usados</h3>
                    {porHorario.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-6 text-center">Sin datos</p>
                    ) : (
                        <div className="p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                            {porHorario.map((row) => {
                                const oc = ocupacionLabel((row.count / Math.max(porHorario[0]?.count || 1, 1)) * 100);
                                return (
                                    <div key={row.hora} className="flex items-center justify-between border border-border rounded-md px-2 py-1.5 text-xs">
                                        <span className="font-mono font-semibold">{row.hora}</span>
                                        <span className="text-muted-foreground">{row.count}</span>
                                        <span className={`text-[10px] font-semibold ${oc.cls}`}>{oc.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function Kpi({ icon: Icon, label, value, testid }) {
    return (
        <Card>
            <CardContent className="p-4 flex items-start gap-3" data-testid={testid}>
                <div className="h-9 w-9 rounded-md bg-primary-soft text-primary grid place-items-center shrink-0">
                    <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
                    <p className="font-heading font-bold text-lg truncate">{value}</p>
                </div>
            </CardContent>
        </Card>
    );
}
